import { UgcProductResponseDto } from '@/app/common/general-types/products.dto';
import { Injectable, signal } from '@angular/core';
import { form, required } from '@angular/forms/signals';
import { MonitorTypeEnum, ScreeningListCreatorItem } from '@/app/common/general-types/social-screening.dto';
import { RecruitGroupResponseDto } from '@/app/common/general-types/recruit-groups.dto';

/** How the brand chose to source creators in the wizard's final step. */
export type MonitoringCreatorSourceMethod = 'one' | 'csv' | 'shout' | 'later';

/** Upload a document, or type the requirements in the wizard. */
export type MonitoringProvisionMode = 'upload' | 'type';

@Injectable({
  providedIn: 'root',
})
export class CreateMonitoring {


  /** Monitoring Type Options */
  monitoringTypeOptions:  {
    type: MonitorTypeEnum,
    name: string,
    description: string,
    mode_details: string[]
  }[] = [
    {
      type: MonitorTypeEnum.CREATOR_BRIEF_CHECK,
      name: 'Find Brief Content',
      description: 'Get notified when creators post content that matches a brief.',
      mode_details: [
        "Watch social channels looking for content that matches a brief.",
        "Get notified when matching content is found.",
        "Review matching posted content.",
        "Decide whether the content stays or is removed."
      ]
    },
    {
      type: MonitorTypeEnum.CREATOR_MONITOR_ALL,
      name: 'Monitor Brand Safety',
      description: 'Get notified when creators post content that is not brand safe.',
      mode_details: [
        "Watch social channels looking for content that is not brand safe.",
        "Get notified when brand unsafe content is found.",
        "Review non-compliant content.",
        "Decide whether the content stays or is removed."
      ]
    }
  ]
  /** Monitoring Type Options - END */
  
  /** Basic Info Values */
  basicInfoModel = signal({
    briefTitle: '',
  })
  basicInfoForm = form(this.basicInfoModel, (schemaPath) => {
    required(schemaPath.briefTitle, { message: 'Brief title is required' });
  })

  selectedProduct = signal<UgcProductResponseDto | undefined>(undefined);

  additionalProductImages = signal<string[]>([]);
  /** Basic Info Values  - END */

  /** Requirements Values */
  requirementsModel = signal({
    requirementsGoogleDocUrl: '',
  })
  requirementsForm = form(this.requirementsModel, (schemaPath) => {
    required(schemaPath.requirementsGoogleDocUrl, { message: 'Requirements Google Doc URL is required' });
  })
  requirementsHtml = signal<string>('');
  requirementsDocumentUrl = signal<string>('');
  briefTextHtml = signal<string>('');
  monitorStart = signal<Date | undefined>(undefined);
  monitorUntil = signal<Date | undefined>(undefined);
  /** Requirements Values - END */

  /** Restrictions Values */
  restrictionsModel = signal({
    restrictionsGoogleDocUrl: '',
  })
  restrictionsForm = form(this.restrictionsModel, (schemaPath) => {
    required(schemaPath.restrictionsGoogleDocUrl, { message: 'Restrictions Google Doc URL is required' });
  })
  restrictionsHtml = signal<string>('');
  restrictionsDocumentUrl = signal<string>('');
  /** Restrictions Values - END */

  /** Compliance Values */
  selectedComplianceIds = signal<string[]>([]);
  /** Compliance Values - END */

  /** What type of monitoring would you like to do? */
  monitorType = signal<MonitorTypeEnum>(MonitorTypeEnum.CREATOR_BRIEF_CHECK);
  /** What type of monitoring would you like to do? - END */

  /** Do you want to be notified of publication only? Or do you want to review published content too? */
  publishedOnly = signal<boolean>(false);

  /** Do you want to analyze comments on the content? */
  commentAnalysis = signal<boolean>(false);

  /** Creator List Values */
  /** The recruit group chosen for the brief. Groups replaced the old screening lists. */
  selectedList = signal<RecruitGroupResponseDto | undefined>(undefined);
  /** How creators are attached. `later` lets the API mint an empty group. */
  creatorSourceMethod = signal<MonitoringCreatorSourceMethod>('later');
  /** Hand-entered rows staged before the brief is posted. */
  manualCreators = signal<ScreeningListCreatorItem[]>([]);
  /** S3 url of an uploaded creator CSV. Imported after the brief exists. */
  uploadedCsvUrl = signal<string | undefined>(undefined);
  /** Creator List Values - END */

  /** Upload a brief document, or type requirements in the wizard. */
  provisionMode = signal<MonitoringProvisionMode>('upload');

  /** Clears wizard state so a second visit starts empty. */
  reset(): void {
    this.basicInfoModel.set({ briefTitle: '' });
    this.selectedProduct.set(undefined);
    this.additionalProductImages.set([]);
    this.requirementsModel.set({ requirementsGoogleDocUrl: '' });
    this.requirementsHtml.set('');
    this.requirementsDocumentUrl.set('');
    this.briefTextHtml.set('');
    this.monitorStart.set(undefined);
    this.monitorUntil.set(undefined);
    this.restrictionsModel.set({ restrictionsGoogleDocUrl: '' });
    this.restrictionsHtml.set('');
    this.restrictionsDocumentUrl.set('');
    this.selectedComplianceIds.set([]);
    this.monitorType.set(MonitorTypeEnum.CREATOR_BRIEF_CHECK);
    this.publishedOnly.set(false);
    this.commentAnalysis.set(false);
    this.selectedList.set(undefined);
    this.creatorSourceMethod.set('later');
    this.manualCreators.set([]);
    this.uploadedCsvUrl.set(undefined);
    this.provisionMode.set('upload');
  }
  
}
